import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Lock, RefreshCw, Phone, MapPin, Bug, Calendar, IndianRupee,
  Mail, Users, CreditCard, CheckCircle2, XCircle, Clock, Tag,
} from 'lucide-react';
import useSeo from '../hooks/useSeo';
import AdminCoupons from './AdminCoupons';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'entorid_admin_token';

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const formatPrice = (p) => {
  if (typeof p === 'number') return `₹${p.toLocaleString('en-IN')}`;
  return p || '—';
};

function StatusPill({ status }) {
  const map = {
    new: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
    paid: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2 },
    created: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
    verification_failed: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
  };
  const cfg = map[status] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.text} text-xs font-semibold uppercase h-fit`}>
      <Icon size={12} />
      {status?.replace(/_/g, ' ') || 'unknown'}
    </span>
  );
}

function LeadRow({ lead }) {
  return (
    <div data-testid={`lead-row-${lead.id}`} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-wrap justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{lead.name}</h3>
          <div className="flex items-center gap-1 text-sm text-gray-500"><Calendar size={12} />{formatDate(lead.created_at)}</div>
        </div>
        <StatusPill status={lead.status} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
        <div className="flex items-center gap-2"><Phone size={14} className="text-gray-400" /><a href={`tel:${lead.phone}`} className="text-gray-700 hover:text-green-600">{lead.phone}</a></div>
        <div className="flex items-center gap-2"><MapPin size={14} className="text-gray-400" /><span className="text-gray-700">{lead.city}</span></div>
        <div className="flex items-center gap-2"><Bug size={14} className="text-gray-400" /><span className="text-gray-700">{lead.pest_label || lead.pest_type}</span></div>
        <div className="flex items-center gap-2"><IndianRupee size={14} className="text-gray-400" /><span className="font-semibold text-gray-900">{formatPrice(lead.estimated_price)}</span></div>
      </div>
      {lead.email && <div className="mt-2 text-sm text-gray-600 flex items-center gap-2"><Mail size={13} /> {lead.email}</div>}
      {(lead.property_size || lead.additional_details || lead.pest_description) && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-600 space-y-1">
          {lead.property_size && <p><span className="font-medium">Property:</span> {lead.property_size}</p>}
          {lead.pest_description && <p><span className="font-medium">Pest issue:</span> {lead.pest_description}</p>}
          {lead.additional_details && <p><span className="font-medium">Notes:</span> {lead.additional_details}</p>}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <a href={`https://wa.me/91${lead.phone.replace(/\D/g, '').slice(-10)}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-md bg-green-50 text-green-700 hover:bg-green-100 font-medium">WhatsApp</a>
        <a href={`tel:${lead.phone}`} className="text-xs px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium">Call</a>
      </div>
    </div>
  );
}

function PaymentRow({ p }) {
  return (
    <div data-testid={`payment-row-${p.id}`} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-wrap justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{p.name || 'Anonymous'}</h3>
          <div className="flex items-center gap-1 text-sm text-gray-500"><Calendar size={12} />{formatDate(p.created_at)}</div>
        </div>
        <StatusPill status={p.status} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
        <div className="flex items-center gap-2"><Phone size={14} className="text-gray-400" />{p.phone ? <a href={`tel:${p.phone}`} className="text-gray-700 hover:text-green-600">{p.phone}</a> : <span className="text-gray-400">—</span>}</div>
        <div className="flex items-center gap-2"><Mail size={14} className="text-gray-400" /><span className="text-gray-700 truncate">{p.email || '—'}</span></div>
        <div className="flex items-center gap-2"><CreditCard size={14} className="text-gray-400" /><span className="text-gray-700 text-xs font-mono">{p.razorpay_payment_id || '—'}</span></div>
        <div className="flex items-center gap-2"><IndianRupee size={14} className="text-gray-400" /><span className="font-bold text-gray-900">{formatPrice(p.amount)}</span></div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3 text-xs text-gray-500">
        <div><span className="font-medium text-gray-700">Order ID:</span> <span className="font-mono">{p.order_id}</span></div>
        <div><span className="font-medium text-gray-700">Receipt:</span> <span className="font-mono">{p.receipt}</span></div>
      </div>
      {p.phone && (
        <div className="mt-3 flex gap-2">
          <a href={`https://wa.me/91${p.phone.replace(/\D/g, '').slice(-10)}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-md bg-green-50 text-green-700 hover:bg-green-100 font-medium">WhatsApp</a>
          <a href={`tel:${p.phone}`} className="text-xs px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium">Call</a>
        </div>
      )}
    </div>
  );
}

export default function AdminLeads() {
  useSeo('Admin', 'EntoRid admin dashboard for managing pest control leads and payments.');

  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [tokenInput, setTokenInput] = useState('');
  const [activeTab, setActiveTab] = useState('leads');

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async (authToken) => {
    if (!authToken) return;
    setLoading(true);
    setError('');
    try {
      const headers = { 'X-Admin-Token': authToken };
      const [leadsRes, statsRes, paymentsRes] = await Promise.all([
        axios.get(`${API_BASE}/leads`, { headers }),
        axios.get(`${API_BASE}/leads/stats`, { headers }),
        axios.get(`${API_BASE}/payments`, { headers }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setPayments(paymentsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid admin token');
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      } else {
        setError(err.message || 'Failed to fetch data');
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (token) fetchData(token); }, [token, fetchData]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    localStorage.setItem(TOKEN_KEY, tokenInput.trim());
    setToken(tokenInput.trim());
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setLeads([]);
    setStats({ total: 0, new: 0 });
    setPayments([]);
  };

  // Login screen
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md border border-green-100" data-testid="admin-login-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-green-600 text-white flex items-center justify-center"><Lock size={20} /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Access</h1>
              <p className="text-sm text-gray-500">EntoRid Lead &amp; Payment Dashboard</p>
            </div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="admin-token">Admin Token</Label>
              <Input id="admin-token" data-testid="admin-token-input" type="password" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} placeholder="Enter admin token" className="mt-2 h-12" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="admin-error">{error}</p>}
            <Button type="submit" data-testid="admin-login-btn" className="w-full h-12 bg-green-600 hover:bg-green-700">Sign In</Button>
          </form>
        </div>
      </div>
    );
  }

  // Compute payment stats
  const paymentTotal = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0);
  const paidCount = payments.filter(p => p.status === 'paid').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">EntoRid Admin</h1>
            <p className="text-sm text-gray-500">Real-time leads &amp; payments</p>
          </div>
          <div className="flex items-center gap-3">
            <Button data-testid="refresh-btn" variant="outline" onClick={() => fetchData(token)} disabled={loading}>
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
            <Button data-testid="admin-logout-btn" variant="outline" onClick={handleLogout}>Logout</Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 border-t border-gray-100">
          <div className="flex gap-6">
            <button
              type="button"
              onClick={() => setActiveTab('leads')}
              data-testid="tab-leads"
              className={`flex items-center gap-2 py-3 border-b-2 transition-colors text-sm font-semibold ${
                activeTab === 'leads' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users size={16} />
              Leads <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{leads.length}</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('payments')}
              data-testid="tab-payments"
              className={`flex items-center gap-2 py-3 border-b-2 transition-colors text-sm font-semibold ${
                activeTab === 'payments' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <CreditCard size={16} />
              Payments <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{payments.length}</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('coupons')}
              data-testid="tab-coupons"
              className={`flex items-center gap-2 py-3 border-b-2 transition-colors text-sm font-semibold ${
                activeTab === 'coupons' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Tag size={16} />
              Coupons
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Coupons tab gets its own full panel */}
        {activeTab === 'coupons' ? (
          <AdminCoupons token={token} />
        ) : (
          <>
        {/* Stats */}
        {activeTab === 'leads' ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" data-testid="stat-total">
              <p className="text-sm text-gray-500 mb-1">Total Leads</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" data-testid="stat-new">
              <p className="text-sm text-gray-500 mb-1">New / Unread</p>
              <p className="text-3xl font-bold text-green-600">{stats.new}</p>
            </div>
            <div className="bg-gradient-to-br from-green-600 to-green-700 p-6 rounded-xl text-white shadow-sm">
              <p className="text-sm text-green-100 mb-1">Showing</p>
              <p className="text-3xl font-bold">{leads.length}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" data-testid="stat-payments-revenue">
              <p className="text-sm text-gray-500 mb-1">Total Revenue (Paid)</p>
              <p className="text-3xl font-bold text-gray-900">{formatPrice(paymentTotal)}</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" data-testid="stat-payments-paid">
              <p className="text-sm text-gray-500 mb-1">Successful Payments</p>
              <p className="text-3xl font-bold text-green-600">{paidCount}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-6 rounded-xl text-white shadow-sm">
              <p className="text-sm text-blue-100 mb-1">Total Orders</p>
              <p className="text-3xl font-bold">{payments.length}</p>
            </div>
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-4" data-testid="admin-error">{error}</div>}

        {activeTab === 'leads' && (
          leads.length === 0 && !loading ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center" data-testid="leads-empty">
              <Bug size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No leads yet. They will appear here once customers submit the calculator form.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="leads-list">
              {leads.map((lead) => <LeadRow key={lead.id} lead={lead} />)}
            </div>
          )
        )}

        {activeTab === 'payments' && (
          payments.length === 0 && !loading ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center" data-testid="payments-empty">
              <CreditCard size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No payments yet. Successful Razorpay transactions will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="payments-list">
              {payments.map((p) => <PaymentRow key={p.id} p={p} />)}
            </div>
          )
        )}
          </>
        )}
      </main>
    </div>
  );
}
